export function MarketDemand() {
  const stats = [
    {
      icon: 'ri-money-dollar-circle-line',
      value: '$185-$320',
      label: 'Average Nightly Rate',
      description: 'Vancouver STRs command premium rates year-round'
    },
    {
      icon: 'ri-percent-line',
      value: '75%',
      label: 'Average Occupancy',
      description: 'High demand across all seasons'
    },
    {
      icon: 'ri-calendar-2-line',
      value: '365',
      label: 'Max Rental Days/Year',
      description: 'No night cap for principal residence'
    },
    {
      icon: 'ri-line-chart-line',
      value: '$50K-$88K',
      label: 'Annual Revenue Potential',
      description: 'For compliant 1-2 bedroom properties'
    }
  ];

  const neighborhoods = [
    {
      name: 'Kitsilano',
      demand: 'Very High',
      avgRate: '$250-$300',
      occupancy: '78%',
      notes: 'Beach access, family-friendly, massive demand'
    },
    {
      name: 'Downtown & West End',
      demand: 'Very High',
      avgRate: '$280-$320',
      occupancy: '80%',
      notes: 'Business travelers, tourists, cruise ship visitors'
    },
    {
      name: 'Yaletown & Coal Harbour',
      demand: 'Very High',
      avgRate: '$250-$300',
      occupancy: '75%',
      notes: 'Luxury market, waterfront appeal'
    },
    {
      name: 'Point Grey',
      demand: 'High',
      avgRate: '$220-$280',
      occupancy: '72%',
      notes: 'Beach proximity, upscale residential'
    },
    {
      name: 'Mount Pleasant & Main St',
      demand: 'High',
      avgRate: '$185-$240',
      occupancy: '68%',
      notes: 'Trendy dining, arts scene'
    },
    {
      name: 'Commercial Drive',
      demand: 'Medium-High',
      avgRate: '$170-$220',
      occupancy: '65%',
      notes: 'Cultural diversity, local vibe'
    }
  ];

  const seasonalTrends = [
    {
      season: 'Summer (Jun-Sep)',
      demand: 'Peak',
      rate: '+35% above average',
      drivers: 'Tourism, festivals, cruise season, 2026 World Cup spike'
    },
    {
      season: 'Fall (Sep-Nov)',
      demand: 'High',
      rate: '+15% above average',
      drivers: 'Conference season, fall colors'
    },
    {
      season: 'Winter (Dec-Feb)',
      demand: 'Medium',
      rate: 'Average rates',
      drivers: 'Ski visitors, holiday travel'
    },
    {
      season: 'Spring (Mar-May)',
      demand: 'High',
      rate: '+20% above average',
      drivers: 'Cherry blossoms, spring break'
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Vancouver 2026 STR Market Demand
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Vancouver remains one of Canada's strongest short-term rental markets. Massive demand in Kitsilano and Downtown, with 2026 World Cup travel creating peak summer opportunities for compliant operators.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="bg-[#F0F0F0] rounded-xl p-6 text-center hover:shadow-lg transition-all"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#882629]/10 rounded-full mx-auto mb-4">
                <i className={`${stat.icon} text-2xl text-[#882629]`}></i>
              </div>
              <div className="text-3xl font-bold text-[#882629] mb-2">
                {stat.value}
              </div>
              <div className="text-base font-semibold text-[#505A5A] mb-2">
                {stat.label}
              </div>
              <p className="text-sm text-[#505A5A]/70">
                {stat.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-8 text-center">
            High-Demand Neighborhoods
          </h3>
          <div className="bg-[#F0F0F0] rounded-2xl p-8 shadow-md">
            <div className="space-y-6">
              {neighborhoods.map((area, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 hover:shadow-md transition-all"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-grow">
                      <h4 className="text-xl font-semibold text-[#505A5A] mb-2">
                        {area.name}
                      </h4>
                      <p className="text-sm text-[#505A5A]/70">
                        {area.notes}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-4 lg:gap-6 lg:flex-shrink-0">
                      <div className="text-center">
                        <div className="text-xs text-[#505A5A]/60 mb-1">Demand</div>
                        <div className={`text-sm font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
                          area.demand === 'Very High' ? 'bg-green-100 text-green-800' :
                          area.demand === 'High' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {area.demand}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-[#505A5A]/60 mb-1">Avg Rate</div>
                        <div className="text-sm font-semibold text-[#882629]">{area.avgRate}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-[#505A5A]/60 mb-1">Occupancy</div>
                        <div className="text-sm font-semibold text-[#505A5A]">{area.occupancy}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-8 text-center">
            2026 Seasonal Demand Patterns
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {seasonalTrends.map((trend, index) => (
              <div 
                key={index}
                className="bg-[#F0F0F0] rounded-xl p-6 hover:shadow-lg transition-all"
              >
                <h4 className="text-lg font-semibold text-[#505A5A] mb-3">
                  {trend.season}
                </h4>
                <div className="mb-4">
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    trend.demand === 'Peak' ? 'bg-green-100 text-green-800' :
                    trend.demand === 'High' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {trend.demand} Demand
                  </span>
                </div>
                <div className="text-base font-semibold text-[#882629] mb-3">
                  {trend.rate}
                </div>
                <p className="text-sm text-[#505A5A]/70">
                  {trend.drivers}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-[#882629]/5 border-l-4 border-[#882629] rounded-lg p-8">
          <div className="flex items-start gap-4">
            <i className="ri-information-line text-3xl text-[#882629] flex-shrink-0"></i>
            <div>
              <h4 className="text-xl font-semibold text-[#505A5A] mb-3">
                Maximize Your Vancouver STR Revenue in 2026
              </h4>
              <p className="text-base text-[#505A5A]/70 leading-relaxed mb-4">
                Kitsilano and Downtown see massive demand year-round. The 2026 World Cup creates a summer spike with rates 35% above average. Our dynamic pricing strategies help compliant operators achieve 15-25% higher revenue compared to self-managed properties.
              </p>
              <p className="text-sm text-[#505A5A]/60 italic">
                Revenue estimates based on 2026 market data for compliant, professionally managed properties with valid Provincial Registry Numbers.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
